import React, { forwardRef, type InputHTMLAttributes } from 'react'

// P005: string-union size replaces the native numeric `size` attribute —
// Omit it from the extended HTML attributes (no caller passes the native
// numeric `size` to <Input>, verified by grep).
export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string
  error?: string
  hint?: string
  leftIcon?: React.ReactNode
  /** Inline suffix rendered INSIDE the field's right edge (e.g. "per month",
   * a unit). Pointer-events-none; pass a pr-* in inputClassName to widen the
   * reserved space for long suffixes (default pr-10 fits short ones). */
  rightAddon?: React.ReactNode
  // EP-95: `className` targets the OUTER form-control wrapper (parity with
  // Select — default 'w-full'); `inputClassName` targets the <input> element.
  inputClassName?: string
  // P005: default 'md' maps to '' so default output stays byte-identical.
  size?: 'sm' | 'md'
  ghost?: boolean
  /**
   * EP-114 S1-12 — opt-in override for the required-field asterisk colour.
   * Defaults to the existing `text-error` (#C22826) so every other caller in
   * the app stays byte-identical (D-0b: the current design system is
   * untouched). Callers on the NEW design system (currently only
   * `PropertyGeneralFields`' name field) pass `text-fd-negative` (#D40018)
   * instead.
   */
  asteriskClassName?: string
}

const SIZE_CLASSES: Record<NonNullable<InputProps['size']>, string> = {
  sm: 'input-sm',
  md: '',
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className = '',
      inputClassName = '',
      label,
      error,
      hint,
      leftIcon,
      rightAddon,
      id,
      required,
      size = 'md',
      ghost,
      asteriskClassName = 'text-error',
      ...props
    },
    ref,
  ) => {
    const inputId = id || props.name

    return (
      <div className={`form-control ${className || 'w-full'}`}>
        {label && (
          <label className="label whitespace-normal" htmlFor={inputId}>
            <span className="label-text">
              {label}
              {required && <span className={`${asteriskClassName} ml-0.5`} aria-hidden="true">*</span>}
            </span>
          </label>
        )}
        <div className={leftIcon || rightAddon ? 'relative' : ''}>
          {leftIcon && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/60 z-10 pointer-events-none">
              {leftIcon}
            </span>
          )}
          {rightAddon && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-base-content/60 z-10 pointer-events-none whitespace-nowrap">
              {rightAddon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            required={required}
            className={`input input-bordered w-full ${SIZE_CLASSES[size]} ${ghost ? 'input-ghost' : ''} ${leftIcon ? 'pl-10' : ''} ${rightAddon && !/\bpr-/.test(inputClassName) ? 'pr-10' : ''} ${error ? 'input-error' : ''} ${inputClassName}`}
            {...props}
          />
        </div>
        {(error || hint) && (
          <label className="label whitespace-normal">
            <span className={`label-text-alt ${error ? 'text-error' : ''}`}>
              {error || hint}
            </span>
          </label>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'
